import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import { ButtonV1, Loader, TooltipLabel } from "components";

import Pagination from "./Pagination";

import {
  sentenceCase,
  timeConvertor,
  getMaskData,
  getSubstring,
  formatDate,
  validateNull,
} from "utils";
import { useToaster } from "hooks";
import { getStatusMeaning } from "../makeData";
import {
  notificationHeader,
  notificationStatuses,
  CHANNEL_OPTIONS,
} from "../constant";
import { getCall_v2 } from "services";
import { error } from "constants";

const NotificationTable = ({
  data,
  onPageChange,
  pageNumber,
  isNotificationDataLoading,
  showAttachmentsForChannels = [],
  limit,
}) => {
  const theme = useTheme();
  const triggerToaster = useToaster();

  // Calculate current offset based on pageNumber and limit
  const currentOffset = (pageNumber - 1) * limit;
  const endOffset = currentOffset + limit;

  const getStatusColor = (status = "") => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "success":
        return {
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.dark,
        };
      case "bounce":
      case "dropped":
      case "spamreport":
      case "failed":
        return {
          color: theme.palette.error.errorTextColor,
          borderColor: theme.palette.error.errorTextColor,
        };
      case "deferred":
      case "unsubscribe":
      case "group_unsubscribe":
        return {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.dark,
        };
      case "open":
      case "click":
        return {
          color: theme.palette.info.light,
          borderColor: theme.palette.info.light,
        };
      case "processed":
      case "pending":
      default:
        return {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.dark,
        };
    }
  };

  const handleOpenPDF = (base64) => {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      const newWindow = window.open(blobUrl);
      if (!newWindow) {
        triggerToaster(
          "Popup blocked! Please enable popups for this site.",
          error,
        );
      }
    } catch (err) {
      console.error("Error opening PDF:", err);
      triggerToaster("Failed to open PDF. Please try again.", error);
    }
  };

  const getTemplateData = async (eventId, channel) => {
    try {
      const { data } = await getCall_v2(
        `/oms/notification/event-id/${eventId}?channel=${channel}`,
      );
      if (data) {
        handleOpenPDF(data?.data.pdfData?.base64);
      } else {
        triggerToaster("Error while fetching template data", error);
      }
    } catch (err) {
      triggerToaster(
        err.message ?? "Error while fetching template data",
        error,
      );
    }
  };

  const handleAttachments = async (data) => {
    try {
      const attachments = data?.attachments;
      const eventId = data?.event_id;
      if (attachments?.length) {
        const attachmentsNames = attachments.join(",");
        const res = await getCall_v2(
          `/joms/api/external/v1/notification/get-attachment-signed-url?eventId=${eventId}&fileNames=${attachmentsNames}`,
        );
        const result = res?.data?.data?.data;
        if (result?.length) {
          result?.forEach((attachment) => {
            window.open(attachment?.signedUrl, "_blank");
          });
        } else {
          triggerToaster("Error while fetching template data", error);
        }
      }
    } catch (err) {
      triggerToaster(
        err.message ?? "Error while fetching template data",
        error,
      );
    }
  };

  const { hasNextPage, hasPreviousPage } = data || {};

  return (
    <Box sx={{ mb: 2, width: "calc(100vw - 96px)", overflowX: "auto" }}>
      <TableContainer
        sx={{
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1800 }}>
          <TableHead>
            <TableRow>
              {notificationHeader.map((item) =>
                item === "Email/Mobile" ? (
                  <TableCell key={item}>
                    <Box display={"flex"} alignItems={"center"}>
                      {item}
                      <TooltipLabel
                        placement="right"
                        title={notificationStatuses.map((status) => (
                          <Box key={status?.status} display={"flex"} gap={1}>
                            <Typography fontWeight={"bold"}>
                              {sentenceCase(status.status)} :
                            </Typography>
                            <Typography>{status?.meaning}</Typography>
                          </Box>
                        ))}
                        labelChildren={
                          <InfoOutlined
                            style={{
                              width: 16,
                              marginLeft: 6,
                              marginTop: 4,
                            }}
                            color={"primary"}
                          />
                        }
                      />
                    </Box>
                  </TableCell>
                ) : (
                  <TableCell key={item}>{item}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isNotificationDataLoading ? (
              <TableRow>
                <TableCell colSpan={notificationHeader.length}>
                  <Loader sx={{ margin: 0 }} />
                </TableCell>
              </TableRow>
            ) : data?.data?.length ? (
              data?.data?.map((row) => (
                <TableRow key={`${row.event_id}-${row.channel}`}>
                  <TableCell>
                    <Typography>
                      {formatDate(row?.request_timestamp * 1000)}
                    </Typography>
                    <Typography variant="caption">
                      {timeConvertor(row?.request_timestamp * 1000)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {formatDate(row?.last_status_timestamp * 1000)}
                    </Typography>
                    <Typography variant="caption">
                      {timeConvertor(row?.last_status_timestamp * 1000)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row?.channel === CHANNEL_OPTIONS.Whatsapp
                      ? getMaskData(row?.receiver)
                      : validateNull(getSubstring(row?.receiver, 40))}
                  </TableCell>

                  <TableCell>
                    {row?.provider_status ? (
                      <Box display="flex" alignItems="center">
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={getStatusColor(row.provider_status)}
                          label={sentenceCase(row.provider_status)}
                        />

                        {getStatusMeaning(row.provider_status) && (
                          <TooltipLabel
                            placement="bottom"
                            title={
                              <Typography>
                                {row.provider_status !== "failed"
                                  ? getStatusMeaning(row.provider_status)
                                      .meaning
                                  : row?.failure_reason}
                              </Typography>
                            }
                            labelChildren={
                              <InfoOutlined
                                sx={{
                                  width: 16,
                                  ml: 0.75,
                                  mt: 0.5,
                                }}
                                color="primary"
                              />
                            }
                          />
                        )}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {validateNull(sentenceCase(row?.entity_type))}
                  </TableCell>

                  <TableCell>
                    <ButtonV1
                      size="small"
                      title="Show template"
                      onClick={() =>
                        getTemplateData(row?.event_id, row.channel)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    {showAttachmentsForChannels.includes(row?.channel) &&
                    row?.attachments?.length ? (
                      <ButtonV1
                        size="small"
                        title="Download"
                        onClick={() => handleAttachments(row)}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{validateNull(row?.template_type)}</TableCell>
                  <TableCell>{validateNull(row?.reference)}</TableCell>
                  <TableCell>{validateNull(row?.channel)}</TableCell>
                  <TableCell>{validateNull(row?.event_id)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={notificationHeader.length} align="center">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        currentOffset={currentOffset}
        endOffset={endOffset}
        pageNumber={pageNumber}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

export default NotificationTable;
